import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { Order, Users as UsersService, Region, CakeBox } from '../../../../client/services';
import { Users } from '../../../../store/users/api/interfaces';
import { CustomCakeData } from './CustomCakeBuilder';
import { SelectedAddon } from './AddonSelector';
import { Address } from '../../../../client/services';
import uuidv4 from '../../../../utils/uuidv4';
import { toast } from 'react-toastify';

interface DeliveryData {
  addressId: string | null;
  deliveryDate: string | null;
  deliveryTimeSlot: string | null;
  isIdentitySecret: boolean;
  recipientName: string | null;
  recipientPhone: string | null;
}

interface PaymentSelectorProps {
  selectedCustomer: Users | null;
  selectedCakes: CustomCakeData[];
  selectedAddons: SelectedAddon[];
  deliveryData: DeliveryData | null;
  onOrderCreated?: () => void;
}

// Payment Method IDs (from backend seed data)
const COD_PAYMENT_METHOD_ID = '705d822a-e2f9-4793-9cbf-605330a49d32'; // Cash on Delivery
const PAID_PAYMENT_METHOD_ID = 'f47ac10b-58cc-4372-a567-0e02b2c3d479'; // Paid
const ONLINE_PAYMENT_METHOD_ID = 'b83d8a4c-8c16-46a1-bc2d-ddbc28ef331d'; // Online

const paymentMethodIdFor = (method: 'paid' | 'cod' | 'online') => {
  if (method === 'paid') return PAID_PAYMENT_METHOD_ID;
  if (method === 'online') return ONLINE_PAYMENT_METHOD_ID;
  return COD_PAYMENT_METHOD_ID;
};

const PaymentSelector: React.FC<PaymentSelectorProps> = ({
  selectedCustomer,
  selectedCakes,
  selectedAddons,
  deliveryData,
  onOrderCreated,
}) => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState<any>(null);
  const [region, setRegion] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'paid' | 'cod' | 'online'>('cod');
  const [cakeBoxes, setCakeBoxes] = useState<any[]>([]);
  const [paymentLinkDialog, setPaymentLinkDialog] = useState<{
    open: boolean;
    url: string;
  }>({ open: false, url: '' });

  useEffect(() => {
    const loadCakeBoxes = async () => {
      try {
        const result = await CakeBox.find({
          query: { activeOnly: true, limit: 50, skip: 0 },
        });
        setCakeBoxes(result?.data ?? []);
      } catch (err) {
        console.error('Error fetching cake boxes:', err);
      }
    };
    loadCakeBoxes();
  }, []);

  // Fetch user with region data and address details
  useEffect(() => {
    const fetchData = async () => {
      if (selectedCustomer?.id) {
        try {
          // Fetch user with region included
          const userData = await UsersService.get(selectedCustomer.id);
          if (userData?.region) {
            setRegion(userData.region);
          } else if (userData?.regionCode) {
            // If region is not included, fetch it by code
            try {
              const regionData = await Region.find({ query: { code: userData.regionCode } });
              if (regionData?.data && regionData.data.length > 0) {
                setRegion(regionData.data[0]);
              }
            } catch (regionErr) {
              console.error('Error fetching region:', regionErr);
            }
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        }
      }

      // Fetch address details if addressId is provided
      if (deliveryData?.addressId) {
        try {
          const addr = await Address.get(deliveryData.addressId);
          setAddress(addr);
        } catch (err) {
          console.error('Error fetching address:', err);
        }
      }
    };
    fetchData();
  }, [selectedCustomer?.id, deliveryData?.addressId]);

  // Calculate subtotal (cakes + addons + optional cake box)
  const cakesSubtotal = selectedCakes.reduce((sum, cake) => sum + cake.unitPrice * cake.qty, 0);
  const addonsSubtotal = selectedAddons.reduce((sum, addon) => sum + addon.unitPrice, 0);
  const cakePackagingLines = selectedCakes
    .map((cake, index) => {
      const box = cakeBoxes.find((b) => b.id === cake.cakeBoxId);
      if (!box) return null;
      return {
        index: index + 1,
        labelEn: box.labelEn,
        price: Number(box.price || 0) * Number(cake.qty || 1),
      };
    })
    .filter(Boolean) as Array<{ index: number; labelEn: string; price: number }>;
  const cakeBoxPrice = cakePackagingLines.reduce(
    (sum, line) => sum + line.price,
    0
  );
  const subtotal = cakesSubtotal + addonsSubtotal + cakeBoxPrice;

  // Calculate delivery fee and VAT from region data
  // Default delivery fee is 15 QAR if not available from region
  const deliveryFee = region?.deliveryFee ? parseFloat(region.deliveryFee) : 15;
  const vatRate = region?.vatRate ? parseFloat(region.vatRate) : 0;
  const vatAmount = (subtotal * vatRate) / 100;
  const total = subtotal + deliveryFee + vatAmount;

  // Format delivery date for display
  const formatDeliveryDate = () => {
    if (!deliveryData?.deliveryDate) return 'Not set';
    try {
      const date = new Date(deliveryData.deliveryDate);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return deliveryData.deliveryDate;
    }
  };

  // Create order
  const handleCreateOrder = async () => {
    if (!selectedCustomer || !deliveryData || selectedCakes.length === 0) {
      setError('Missing required information');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Format delivery date as JSON string (required by backend)
      // Backend expects: { type: "date", date: "ISO_DATE_STRING", time: "10 am to 2 pm" }
      const deliveryDateObj = {
        type: 'date',
        date: deliveryData.deliveryDate || new Date().toISOString(),
        time: deliveryData.deliveryTimeSlot || '',
      };
      const deliveryDateJson = JSON.stringify(deliveryDateObj);

      // Format recipient info if it's a gift order
      let recipientInfoJson: string | undefined = undefined;
      if (deliveryData.isIdentitySecret && deliveryData.recipientName && deliveryData.recipientPhone) {
        recipientInfoJson = JSON.stringify({
          name: deliveryData.recipientName,
          phone: deliveryData.recipientPhone,
        });
      }

      // Prepare addon product items
      const addonProductItems = selectedAddons.map((addon) => ({
        addonProductId: addon.addonProductId,
        qty: addon.qty,
        unitPrice: addon.price, // Use base price, not unitPrice (which includes qty)
      }));

      // Prepare order cake items
      const orderCakeItems = selectedCakes.map((cake) => {
        // Store both image URLs as JSON in printImageUrl field (no DB schema change needed)
        // Format: {"printImageUrl": "...", "customImageUrl": "..."} or just the printImageUrl string for backward compatibility
        let printImageUrlValue: string | null = null;
        if (cake.printImageUrl || cake.customImageUrl) {
          const imageData: { printImageUrl?: string | null; customImageUrl?: string | null } = {};
          if (cake.printImageUrl) {
            imageData.printImageUrl = cake.printImageUrl;
          }
          if (cake.customImageUrl) {
            imageData.customImageUrl = cake.customImageUrl;
          }
          printImageUrlValue = JSON.stringify(imageData);
        }

        return {
        id: cake.id || uuidv4(), // Use UUID instead of timestamp-based ID
        shapeLabelEn: cake.shapeLabelEn,
        shapeLabelAr: cake.shapeLabelAr,
        flavorLabelEn: cake.flavorLabelEn,
        flavorLabelAr: cake.flavorLabelAr,
        colorCode: cake.colorCode,
        writeOnCake: cake.writeOnCake || null,
        instruction: cake.instruction || null,
          printImageUrl: printImageUrlValue,
        displayMode: cake.displayMode,
        shapeId: cake.shapeId,
        flavorId: cake.flavorId,
        toppingId: cake.toppingId || null,
        categoryId: cake.categoryId || null,
        categoryCakeId: cake.categoryCakeId || null,
        qty: cake.qty,
        unitPrice: cake.unitPrice,
        cakeBoxId: cake.cakeBoxId || null,
        };
      });

      // Create order payload
      // Only include recipientInfo if it's a gift order (isIdentitySecret is true)
      const orderData: any = {
        userId: selectedCustomer.id, // Set the selected customer as the order owner
        paymentMethodId: paymentMethodIdFor(selectedPaymentMethod),
        addressId: deliveryData.addressId,
        deliveryDate: deliveryDateJson,
        isIdentitySecret: deliveryData.isIdentitySecret,
        orderCakeItems: orderCakeItems,
        addonProductItems: addonProductItems,
        walletCreditUsed: 0, // TODO: Add wallet credit support if needed
        promoCode: null, // TODO: Add promo code support if needed
        promoCodeDiscountAmount: 0, // TODO: Calculate from promo code
        additionalInfo: null, // TODO: Add additional info field if needed
      };

      // Paid marks payment as paid; COD and Online stay pending (Online waits for Dibsy)
      if (selectedPaymentMethod === 'paid') {
        orderData.paymentStatus = 'paid';
      }

      // Only include recipientInfo if it's a gift order (required when isIdentitySecret is true)
      if (recipientInfoJson !== undefined) {
        orderData.recipientInfo = recipientInfoJson;
      }

      // Call order creation API
      const createdOrder: any = await Order.create(orderData);

      if (onOrderCreated) {
        onOrderCreated();
      }

      if (selectedPaymentMethod === 'online') {
        if (createdOrder?.paymentError) {
          toast.warn(
            `Order created (temporary), but payment link failed: ${createdOrder.paymentError}`
          );
          navigate('/temp-order');
          return;
        }
        if (createdOrder?.paymentUrl) {
          setPaymentLinkDialog({ open: true, url: createdOrder.paymentUrl });
          toast.success('Order created. Payment link sent via WhatsApp.');
          return;
        }
        toast.warn(
          'Order created as temporary, but no payment link was returned.'
        );
        navigate('/temp-order');
        return;
      }

      // Paid / COD → normal Orders list
      navigate('/order');
    } catch (err: any) {
      console.error('Error creating order:', err);
      setError(err?.message || 'Failed to create order. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLinkDialog.url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Box>
      <Card sx={{ p: 3 }}>
        <Typography variant='h5' gutterBottom sx={{ mb: 3 }}>
          Order Confirmation & Payment
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column: Order Details */}
          <Grid item xs={12} md={8}>
            {/* Customer Information */}
            <Card variant='outlined' sx={{ p: 2, mb: 2 }}>
              <Typography variant='h6' gutterBottom>
                Customer Information
              </Typography>
              <Typography variant='body2'>
                <strong>Name:</strong> {selectedCustomer?.firstName} {selectedCustomer?.lastName}
              </Typography>
              <Typography variant='body2'>
                <strong>Email:</strong> {selectedCustomer?.email || 'N/A'}
              </Typography>
              <Typography variant='body2'>
                <strong>Phone:</strong> {selectedCustomer?.countryCallingCode}-{selectedCustomer?.mobileNumber}
              </Typography>
            </Card>

            {/* Delivery Information */}
            <Card variant='outlined' sx={{ p: 2, mb: 2 }}>
              <Typography variant='h6' gutterBottom>
                Delivery Information
              </Typography>
              {deliveryData?.isIdentitySecret ? (
                <>
                  <Chip label='Gift Order' color='secondary' size='small' sx={{ mb: 1 }} />
                  <Typography variant='body2'>
                    <strong>Recipient Name:</strong> {deliveryData.recipientName || 'N/A'}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>Recipient Phone:</strong> {deliveryData.recipientPhone || 'N/A'}
                  </Typography>
                </>
              ) : (
                <>
                  {address && (
                    <>
                      <Typography variant='body2'>
                        <strong>Address Name:</strong> {address.addressName || 'N/A'}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Street:</strong> {address.street || 'N/A'}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Zone:</strong> {address.zone || 'N/A'}
                      </Typography>
                      {address.buildingNumber && (
                        <Typography variant='body2'>
                          <strong>Building:</strong> {address.buildingNumber}
                        </Typography>
                      )}
                      {address.apartmentNumber && (
                        <Typography variant='body2'>
                          <strong>Apartment:</strong> {address.apartmentNumber}
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant='body2'>
                <strong>Delivery Date:</strong> {formatDeliveryDate()}
              </Typography>
              <Typography variant='body2'>
                <strong>Time Slot:</strong> {deliveryData?.deliveryTimeSlot || 'Not set'}
              </Typography>
            </Card>

            {/* Order Items */}
            <Card variant='outlined' sx={{ p: 2, mb: 2 }}>
              <Typography variant='h6' gutterBottom>
                Order Items ({selectedCakes.length + selectedAddons.length})
              </Typography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell align='right'>Qty</TableCell>
                      <TableCell align='right'>Unit Price</TableCell>
                      <TableCell align='right'>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Cakes */}
                    {selectedCakes.map((cake, index) => (
                      <TableRow key={cake.id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Cake
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            <strong>Shape:</strong> {cake.shapeLabelEn} ({cake.shapeLabelAr})
                          </Typography>
                          <Typography variant='body2'>
                            <strong>Flavor:</strong> {cake.flavorLabelEn} ({cake.flavorLabelAr})
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant='body2'>
                              <strong>Color:</strong>
                            </Typography>
                          <Box
                            sx={{
                                width: 20,
                                height: 20,
                              backgroundColor: cake.colorCode,
                              border: '1px solid #ccc',
                              borderRadius: 1,
                            }}
                          />
                          </Box>
                          {cake.toppingLabelEn && (
                            <Typography variant='body2'>
                              <strong>Topping:</strong> {cake.toppingLabelEn} ({cake.toppingLabelAr})
                              </Typography>
                          )}
                          {(cake.cakeBoxLabelEn ||
                            cakeBoxes.find((box) => box.id === cake.cakeBoxId)?.labelEn) && (
                            <Typography variant='body2'>
                              <strong>Packaging:</strong>{' '}
                              {cake.cakeBoxLabelEn ||
                                cakeBoxes.find((box) => box.id === cake.cakeBoxId)
                                  ?.labelEn}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='right'>{cake.qty}</TableCell>
                        <TableCell align='right'>{cake.unitPrice.toFixed(2)} QAR</TableCell>
                        <TableCell align='right'>
                          {(cake.unitPrice * cake.qty).toFixed(2)} QAR
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Addons */}
                    {selectedAddons.map((addon, index) => (
                      <TableRow key={addon.id || `addon-${index}`}>
                        <TableCell>{selectedCakes.length + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Addon
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {addon.labelEn}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {addon.labelAr}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>{addon.qty}</TableCell>
                        <TableCell align='right'>{addon.price.toFixed(2)} QAR</TableCell>
                        <TableCell align='right'>
                          {addon.unitPrice.toFixed(2)} QAR
                        </TableCell>
                      </TableRow>
                    ))}
                    {selectedCakes.length === 0 && selectedAddons.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align='center'>
                          <Typography variant='body2' color='text.secondary'>
                            No items in order
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Right Column: Payment & Summary */}
          <Grid item xs={12} md={4}>
            <Card variant='outlined' sx={{ p: 2, position: 'sticky', top: 20 }}>
              <Typography variant='h6' gutterBottom>
                Payment Method
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={selectedPaymentMethod === 'paid' ? 'contained' : 'outlined'}
                    onClick={() => setSelectedPaymentMethod('paid')}
                    fullWidth
                    sx={{
                      backgroundColor: selectedPaymentMethod === 'paid' ? '#EE3750 !important' : 'transparent',
                      color: selectedPaymentMethod === 'paid' ? '#fff !important' : '#EE3750',
                      borderColor: '#EE3750',
                      '&:hover': {
                        backgroundColor: selectedPaymentMethod === 'paid' ? '#EE3750 !important' : '#FDF0F1',
                        borderColor: '#EE3750',
                      },
                    }}
                  >
                    Paid
                  </Button>
                  <Button
                    variant={selectedPaymentMethod === 'cod' ? 'contained' : 'outlined'}
                    onClick={() => setSelectedPaymentMethod('cod')}
                    fullWidth
                    sx={{
                      backgroundColor: selectedPaymentMethod === 'cod' ? '#EE3750 !important' : 'transparent',
                      color: selectedPaymentMethod === 'cod' ? '#fff !important' : '#EE3750',
                      borderColor: '#EE3750',
                      '&:hover': {
                        backgroundColor: selectedPaymentMethod === 'cod' ? '#EE3750 !important' : '#FDF0F1',
                        borderColor: '#EE3750',
                      },
                    }}
                  >
                    COD
                  </Button>
                </Box>
                <Button
                  variant={selectedPaymentMethod === 'online' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedPaymentMethod('online')}
                  fullWidth
                  sx={{
                    backgroundColor: selectedPaymentMethod === 'online' ? '#EE3750 !important' : 'transparent',
                    color: selectedPaymentMethod === 'online' ? '#fff !important' : '#EE3750',
                    borderColor: '#EE3750',
                    '&:hover': {
                      backgroundColor: selectedPaymentMethod === 'online' ? '#EE3750 !important' : '#FDF0F1',
                      borderColor: '#EE3750',
                    },
                  }}
                >
                  Online Payment
                </Button>
              </Box>

              <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
                Order Summary
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant='body2'>Subtotal:</Typography>
                  <Typography variant='body2'>{(cakesSubtotal + addonsSubtotal).toFixed(2)} QAR</Typography>
                </Box>
                {cakePackagingLines.map((line) => (
                  <Box
                    key={`packaging-${line.index}`}
                    sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
                  >
                    <Typography variant='body2'>
                      Packaging Cake {line.index} ({line.labelEn}):
                    </Typography>
                    <Typography variant='body2'>
                      {line.price.toFixed(2)} QAR
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant='body2'>Delivery Fee:</Typography>
                  <Typography variant='body2'>{deliveryFee.toFixed(2)} QAR</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant='body2'>VAT ({vatRate.toFixed(2)}%):</Typography>
                  <Typography variant='body2'>{vatAmount.toFixed(2)} QAR</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>Total:</Typography>
                  <Typography variant='h6' color='primary'>
                    {total.toFixed(2)} QAR
                  </Typography>
                </Box>
              </Box>

              <Button
                variant='contained'
                fullWidth
                size='large'
                onClick={handleCreateOrder}
                disabled={isCreating || !selectedCustomer || !deliveryData || selectedCakes.length === 0}
                sx={{
                  backgroundColor: '#EE3750 !important',
                  '&:hover': {
                    backgroundColor: '#EE3750 !important',
                  },
                  '&:disabled': {
                    backgroundColor: '#c4cdd5',
                    color: '#fff',
                  },
                  mt: 2,
                }}
              >
                {isCreating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                    Creating Order...
                  </>
                ) : (
                  'Create Order'
                )}
              </Button>
            </Card>
          </Grid>
        </Grid>
      </Card>

      <Dialog
        open={paymentLinkDialog.open}
        onClose={() => {
          setPaymentLinkDialog({ open: false, url: '' });
          navigate('/temp-order');
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Payment link created</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            The order is in Temporary Orders until the customer pays. A WhatsApp
            payment link was sent to the customer. You can also copy the link below.
          </Typography>
          <TextField
            fullWidth
            size='small'
            value={paymentLinkDialog.url}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton
                  aria-label='Copy payment link'
                  onClick={handleCopyPaymentLink}
                  edge='end'
                >
                  <ContentCopyIcon fontSize='small' />
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyPaymentLink}>Copy link</Button>
          <Button
            variant='contained'
            onClick={() => {
              setPaymentLinkDialog({ open: false, url: '' });
              navigate('/temp-order');
            }}
            sx={{
              backgroundColor: '#EE3750 !important',
              '&:hover': { backgroundColor: '#EE3750 !important' },
            }}
          >
            View Temporary Orders
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentSelector;

